import { Bell, Clapperboard, HardDrive, Smartphone, Sparkles } from 'lucide-react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { useIsAdmin } from '@/api/endpoints/me'
import { SETTINGS_TABS, type SettingsSearch, type SettingsTab } from '@/app/routes/_search'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { readRememberedTab, TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/Tabs'
import { NotificationEventsSettings } from '@/features/notifications/components/NotificationEventsSettings'
import { DiskRetentionSection } from '@/features/settings/components/DiskRetentionSection'
import { GlobalDefaultsForm } from '@/features/settings/components/GlobalDefaultsForm'
import { PhoneCheckPanel } from '@/features/settings/components/PhoneCheckPanel'
import { PhoneLockPanel } from '@/features/settings/components/PhoneLockPanel'
import { StudioCoversSwitch } from '@/features/settings/components/StudioCoversSwitch'
import styles from './SettingsPage.module.scss'

/** First tab, and the one a bare ``/settings`` opens. */
const DEFAULT_TAB: SettingsTab = 'generation'

/** Names this page's tabs: the last one picked, and their dragged order. */
const TABS_KEY = 'settings'

/**
 * Settings — system-wide configuration, one tab per area. Per-channel
 * stuff (YouTube OAuth, Discord webhook, channel CRUD) lives at
 * ``/channels``. The Geração / Vídeos / Notificações forms persist to
 * ``global_settings``, each saving only its own fields. The scheduler
 * global pause toggle lives on the Scheduler page, next to the cycle
 * cards — keep it there for context, not here.
 */
export function SettingsPage() {
  const navigate = useNavigate({ from: '/settings' })
  const search = useSearch({ from: '/_app/settings' })
  const isAdmin = useIsAdmin()
  const requestedTab: SettingsTab = search.tab ?? readRememberedTab(TABS_KEY, SETTINGS_TABS) ?? DEFAULT_TAB
  // A aba do celular é só do admin; quem não é cai na primeira.
  const activeTab: SettingsTab = requestedTab === 'device' && !isAdmin ? DEFAULT_TAB : requestedTab

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Conta"
        title="Configurações"
        description="Configurações globais da aplicação. Configurações por canal vivem em /channels."
      />

      {/* forceMount: every tab stays mounted (hidden by CSS when inactive) so
          a form keeps what the user typed while they look at another tab. */}
      <TabsRoot
        persistKey={TABS_KEY}
        value={activeTab}
        onValueChange={(value) => {
          void navigate({
            to: '/settings',
            search: (prev) => {
              const next: SettingsSearch = { ...prev, tab: value as SettingsTab }
              if (next.tab === DEFAULT_TAB) delete next.tab
              return next
            },
          })
        }}
      >
        <TabsList aria-label="Seções de configurações">
          <TabsTrigger value="generation" leadingIcon={<Sparkles />}>Geração</TabsTrigger>
          <TabsTrigger value="videos" leadingIcon={<Clapperboard />}>Vídeos</TabsTrigger>
          <TabsTrigger value="notifications" leadingIcon={<Bell />}>Notificações</TabsTrigger>
          <TabsTrigger value="disk" leadingIcon={<HardDrive />}>Disco</TabsTrigger>
          {isAdmin ? (
            <TabsTrigger value="device" leadingIcon={<Smartphone />}>Prism Device</TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="generation" forceMount className={styles.tabContent}>
          <Section
            title="Geração"
            description="Modelo de IA de nuvem, idioma padrão e o Ollama do desktop."
          >
            <GlobalDefaultsForm section="generation" />
          </Section>
        </TabsContent>

        <TabsContent value="videos" forceMount className={styles.tabContent}>
          <Section title="Vídeos" description="Estilo de legenda, de thumbnail e os narradores padrão.">
            <GlobalDefaultsForm section="videos" />
          </Section>
        </TabsContent>

        <TabsContent value="notifications" forceMount className={styles.tabContent}>
          <Section title="Alertas" description="Limiares que disparam os avisos de custo e de duração no Discord.">
            <GlobalDefaultsForm section="notifications" />
          </Section>
          {/* Os eventos e o teste de envio mexem nos alertas do operador: só o admin. */}
          {isAdmin ? <NotificationEventsSettings /> : null}
        </TabsContent>

        <TabsContent value="disk" forceMount className={styles.tabContent}>
          <Section
            title="Disco e retenção"
            description="Cache de clipes, limpeza diária de não-publicados e purga manual de vídeos já publicados."
          >
            <DiskRetentionSection />
          </Section>
        </TabsContent>

        {isAdmin ? (
          <TabsContent value="device" forceMount className={styles.tabContent}>
            <Section
              title="Celular do Prism"
              description="O aparelho no USB do servidor que aplica a capa dos Shorts nos canais sem verificação."
            >
              <StudioCoversSwitch />
              <PhoneCheckPanel />
              <PhoneLockPanel />
            </Section>
          </TabsContent>
        ) : null}
      </TabsRoot>
    </div>
  )
}
